import type React from "react";
import { Navigate, useLocation } from "react-router";

import { useCenter } from "../../../center";
import { useCenterSubscription } from "../../../subscription";

/**
 * Nothing happens in a centre that has not bought its seats.
 *
 * A centre signs itself up now, so the account on the other side of the form
 * has a centre, an administrator and no capacity at all: no seat carries the
 * plan, no tool is handed out, and the panel behind this would be an empty
 * product with a bill nobody has agreed to. The administrator goes to the one
 * screen that can change that — and everything else waits until it has.
 *
 * Only the administrator. A member of the same centre can do nothing about the
 * payment: locking them on a screen whose only button is one they cannot press
 * would be a worse answer than the Basic account they already have.
 *
 * `/center/subscription` itself is never redirected, or this would be a loop,
 * and neither is anything under `/auth` — signing out has to keep working.
 */
const ALLOWED = ["/center/subscription", "/auth", "/profile"];

export default function CenterPaymentGate({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const { membership, isAdmin, loading: loadingCenter } = useCenter();
    const { data: state, isLoading } = useCenterSubscription(membership?.center?.id);

    // Decided only once both answers are in: redirecting on a half-loaded
    // screen would throw somebody out of the page they asked for and back
    // again a moment later.
    if (loadingCenter || isLoading) return <>{children}</>;
    if (!membership || !isAdmin || !state) return <>{children}</>;

    const bought = state.earlyAdopter || state.subscription !== null;
    const allowed = ALLOWED.some((path) => location.pathname.startsWith(path));
    if (bought || allowed) return <>{children}</>;

    return <Navigate to="/center/subscription" replace />;
}
